import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  messagingChannelsApi,
  notificationSettingsQueryKeys,
  type MessagingChannelLine,
} from "../../shared/api/notificationSettings";

/**
 * #19618/#19613 — can the clinic send on Zalo, SMS and Email at all, as Addy Message reports it.
 * One query, shared by the "Kênh gửi" cards and every channel's template picker, so the two never
 * disagree about a channel. 30 seconds matches the server's cache.
 */
export function useMessagingChannels() {
  return useQuery({
    queryKey: notificationSettingsQueryKeys.channels(),
    queryFn: messagingChannelsApi.get,
    staleTime: 30_000,
    retry: false,
  });
}

/** Where the clinic connects each channel. Connecting itself belongs to Addy Message. */
export const CHANNEL_FIX: Record<string, { to: string; label: string }> = {
  ZBS: { to: "/settings/zalo", label: "Xem kết nối Zalo" },
  // #19667 — straight to the provider screen. Entering the provider's keys is an Addy operator's
  // job today, so the label says who acts rather than implying the clinic can fix it there alone.
  SMS: { to: "/messaging?next=/app/sms", label: "Xem nhà cung cấp SMS trên Addy Message" },
  // The server is declared under Email → Máy chủ Email (SMTP) there (#19661); saying so saves a hunt.
  EMAIL: {
    to: "/messaging?next=/app/email-server",
    label: "Mở Addy Message → Email → Máy chủ Email (SMTP)",
  },
};

/**
 * The connection line of one channel: nothing when it is connected and has templates, amber when it
 * cannot send (OFF) or has no approved template, grey when Addy Message could not be asked — never
 * "chưa kết nối" for a question nobody answered.
 */
export function ChannelConnectionLine({
  line,
  showFix = true,
}: {
  line: MessagingChannelLine | undefined;
  showFix?: boolean;
}) {
  if (!line) return null;
  if (line.state === "UNKNOWN") {
    return (
      <span
        className="text-xs text-muted-foreground"
        data-testid={`channel-connection-${line.channel}`}
      >
        Chưa kiểm tra được kết nối với Addy Message.
      </span>
    );
  }
  if (!line.problem) return null;
  const fix = CHANNEL_FIX[line.channel];
  return (
    <span
      role="status"
      className="text-xs text-amber-800 dark:text-amber-400"
      data-testid={`channel-connection-${line.channel}`}
    >
      ⚠️ {line.problem}
      {line.state === "OFF" && " Chưa gửi được tin qua kênh này."}
      {showFix && fix && (
        <>
          {" "}
          <Link to={fix.to} className="underline">
            {fix.label}
          </Link>
        </>
      )}
    </span>
  );
}
